<template>
  <div v-bind="useEpiAttrs(content)" class="content-area">
    <component
      :is="getComponentFunc(block.alias ?? '')"
      v-for="(block, index) in blocks"
      :key="isContentAreaItemOrXhtmlStringContentFragmentResponseModel(block) ? block.id : index"
      v-bind="getComponentProps(block)" />
  </div>
</template>

<script lang="ts" setup>
import type {
  ColumnBannerBlockResponseModel,
  ColumnBlockResponseModel,
  ColumnItemBlockResponseModel,
  ContentAreaItemResponseModel,
  ExpandableBlockResponseModel,
  FeaturedContentBlockResponseModel,
  FieldResponseModelOfContentAreaResponseModel,
  FieldResponseModelOfXhtmlStringResponseModel,
  FineUploaderElementBlockResponseModel,
  FormContainerBlockResponseModel,
  NewsArticlePageResponseModel,
  PersonBlockResponseModel,
  ReadmoreContentBlockResponseModel,
  RichTextBlockResponseModel,
  RowBlockResponseModel,
  RowContentBlockResponseModel,
  SelectionElementBlockResponseModel,
  SubmitButtonElementBlockResponseModel,
  TextInputElementBlockResponseModel,
  TextareaElementBlockResponseModel,
  TwoColumnTextBlockResponseModel,
  XhtmlStringContentFragmentResponseModel,
  XhtmlStringStringFragmentResponseModel,
} from '@api';
import type { CallToActionContainerBlockResponseModel } from '@api/dist/models/CallToActionContainerBlockResponseModel';
import type { DocumentsBlockResponseModel } from '@api/dist/models/DocumentsBlockResponseModel';
import type {
  AddressImageBlock,
  CallToActionContainerBlock,
  ColumnBannerBlock,
  ColumnBlock,
  ColumnBlockItem,
  ContentAreaContentType,
  ContentBlockTypes,
  DocumentsBlock,
  ExpandableBlock,
  FeaturedContentBlock,
  FormContainerBlock,
  GetComponentPropsReturnType,
  NewsArticlePage,
  PersonBlock,
  ReadMoreBlock,
  RichTextBlock,
  RowBlock,
  RowContentBlock,
  SelectionBlock,
  SubmitButtonBlock,
  TextAreaBlock,
  TextInputBlock,
  TwoColumnTextBlock,
  UploadBlock,
  XhtmlItem,
} from '@base/types/models';

interface ContentArea {
  content: ContentAreaContentType;
  getComponentFunc: (alias: string) => Component | null;
}

const props = defineProps<ContentArea>();

const { inEditModeAndEditable } = useLiveEdit();

function isFieldResponseModelOfContentAreaResponseModel(
  item: ContentAreaContentType
): item is FieldResponseModelOfContentAreaResponseModel {
  return (item as FieldResponseModelOfContentAreaResponseModel).value?.content !== undefined;
}

function isFieldResponseModelOfXhtmlStringResponseModel(
  item: ContentAreaContentType
): item is FieldResponseModelOfXhtmlStringResponseModel {
  return (item as FieldResponseModelOfXhtmlStringResponseModel).value?.fragments !== undefined;
}

function isXhtmlStringContentFragmentResponseModel(
  item: XhtmlItem
): item is XhtmlStringContentFragmentResponseModel {
  return (item as XhtmlStringContentFragmentResponseModel).alias !== 'string';
}

function isContentAreaItemOrXhtmlStringContentFragmentResponseModel(
  item: ContentAreaItemResponseModel | XhtmlItem
): item is ContentAreaItemResponseModel | XhtmlStringContentFragmentResponseModel {
  return (
    (item as ContentAreaItemResponseModel).id !== undefined ||
    (item as XhtmlStringContentFragmentResponseModel).id !== undefined
  );
}

const blocks = computed(() => {
  if (isFieldResponseModelOfContentAreaResponseModel(props.content)) {
    return props.content.value?.content;
  }

  if (isFieldResponseModelOfXhtmlStringResponseModel(props.content)) {
    return props.content.value?.fragments;
  }

  return [];
});

const getComponentProps = (
  block:
    | ContentAreaItemResponseModel
    | XhtmlStringContentFragmentResponseModel
    | XhtmlStringStringFragmentResponseModel
    | null
): GetComponentPropsReturnType | null => {
  if (isNullish(block)) {
    return null;
  }

  if (!isXhtmlStringContentFragmentResponseModel(block)) {
    return {
      content: block.content as string,
    };
  }

  const componentSwitch = (): ContentBlockTypes | null => {
    if (isNullish(block.alias)) {
      return null;
    }

    switch (block.alias) {
      case 'AddressImageBlock': {
        const content = block.content as AddressImageBlock;

        return content;
      }
      case 'ColumnBlock': {
        const content = block.content as ColumnBlockResponseModel;

        return {
          title: content.title,
          columnItemBlocks: content.columnItemBlocks,
        } as ColumnBlock;
      }
      case 'ColumnBannerBlock': {
        const content = block.content as ColumnBannerBlockResponseModel;

        return {
          colorTheme: content.colorTheme,
          content: content.content,
          icon: content.icon,
        } as ColumnBannerBlock;
      }
      case 'ColumnItemBlock': {
        const content = block.content as ColumnItemBlockResponseModel;

        return {
          fontColor: content.fontColor,
          link: content.link,
          text: content.text,
          title: content.title,
          backgroundColor: content.backgroundColor,
        } as ColumnBlockItem;
      }
      case 'ContentArea': {
        return block.content as
          | FieldResponseModelOfContentAreaResponseModel
          | FieldResponseModelOfXhtmlStringResponseModel;
      }
      case 'ExpandableBlock': {
        const content = block.content as ExpandableBlockResponseModel;

        return {
          title: content.title,
          contentBlocks: content.contentBlocks,
          iconName: content.iconName,
          open: content.open,
        } as ExpandableBlock;
      }
      case 'FeaturedContentBlock': {
        const content = block.content as FeaturedContentBlockResponseModel;

        return {
          backgroundColour: content.backgroundColour,
          featuredContentArea: content.featuredContentArea,
          title: content.title,
        } as FeaturedContentBlock;
      }
      case 'FineUploaderElementBlock': {
        const content = block.content as FineUploaderElementBlockResponseModel;

        return {
          allowedExtensions: content.allowedExtensions,
          allowMultiple: content.allowMultiple,
          fieldName: content.fieldName,
          labelText: content.labelText,
          maxFileCount: content.maxFileCount,
          placeHolderText: content.placeHolderText,
          sessionId: content.sessionId,
        } as UploadBlock;
      }
      case 'FormContainerBlock': {
        const content = block.content as FormContainerBlockResponseModel;

        return {
          formId: content.formId,
          formTitle: content.formTitle,
          formElements: content.formElements,
          language: content.language,
        } as FormContainerBlock;
      }
      case 'NewsArticlePage': {
        const content = block.content as NewsArticlePageResponseModel;

        return {
          articleImage: content.articleImage,
          body: content.body,
          hideSubNavigation: content.hideSubNavigation,
          mainArea: content.mainArea,
          pageType: content.pageType,
          title: content.title,
          created: content.created,
          excerpt: content.excerpt,
          url: content.url,
        } as NewsArticlePage;
      }
      case 'PersonBlock': {
        const content = block.content as PersonBlockResponseModel;

        return {
          profilePicture: content.profilePicture,
          aboutMe: content.aboutMe,
          contactMe: content.contactMe ?? '',
          email: content.emailAddress,
          firstName: content.firstName,
          middleName: content.middleName,
          lastName: content.lastName,
          image: content.image,
          jobTitle: content.jobTitle,
          moreAboutMe: content.moreAboutMe,
          phoneNumber: content.phoneNumber ?? '',
          quote: content.quote,
          colorTheme: content.colorTheme,
        } as PersonBlock;
      }
      case 'ReadmoreContentBlock': {
        const content = block.content as ReadmoreContentBlockResponseModel;
        return content as ReadMoreBlock;
      }
      case 'RichTextBlock': {
        const content = block.content as RichTextBlockResponseModel;

        return {
          body: content.body,
          media: content.image,
        } as RichTextBlock;
      }
      case 'RowBlock': {
        const content = block.content as RowBlockResponseModel;
        return content as RowBlock;
      }
      case 'RowContentBlock': {
        const content = block.content as RowContentBlockResponseModel;
        return content as RowContentBlock;
      }
      case 'SelectionElementBlock': {
        const content = block.content as SelectionElementBlockResponseModel;

        return {
          allowMultipleChoises: content.allowMultipleChoises,
          fieldName: content.fieldName,
          labelText: content.labelText,
          options: content.options,
          placeHolderText: content.placeHolderText,
          validators: content.validators,
        } as SelectionBlock;
      }
      case 'SubmitButtonElementBlock': {
        const content = block.content as SubmitButtonElementBlockResponseModel;

        return {
          id: content.id,
          imageUrl: content.imageUrl,
          labelText: content.labelText,
        } as SubmitButtonBlock;
      }
      case 'TextareaElementBlock': {
        const content = block.content as TextareaElementBlockResponseModel;

        return {
          fieldName: content.fieldName,
          labelText: content.labelText,
          placeHolderText: content.placeHolderText,
          validators: content.validators,
        } as TextAreaBlock;
      }
      case 'TextInputElementBlock': {
        const content = block.content as TextInputElementBlockResponseModel;

        return {
          fieldName: content.fieldName,
          labelText: content.labelText,
          placeHolderText: content.placeHolderText,
          validators: content.validators,
          maxLength: content.maxLength,
          predefinedValue: content.predefinedValue,
        } as TextInputBlock;
      }
      case 'TwoColumnTextBlock': {
        const content = block.content as TwoColumnTextBlockResponseModel;

        return {
          firstColumnBody: content.firstColumnBody,
          secondColumnBody: content.secondColumnBody,
          firstColumnTitle: content.firstColumnTitle,
          secondColumnTitle: content.secondColumnTitle,
        } as TwoColumnTextBlock;
      }
      case 'CallToActionContainerBlock': {
        const content = block.content as CallToActionContainerBlockResponseModel;

        return {
          backgroundColor: content.backgroundColor,
          introText: content.introText,
          title: content.title,
          ctaBlocks: content.ctaBlocks,
        } as CallToActionContainerBlock;
      }
      case 'DocumentsBlock': {
        const content = block.content as DocumentsBlockResponseModel;

        return {
          documentLists: content.documentLists,
          title: content.title,
        } as DocumentsBlock;
      }
      default: {
        return null;
      }
    }
  };

  const componentProps = componentSwitch();

  return {
    ...componentProps,
    ...(inEditModeAndEditable.value && { 'data-epi-block-id': block.id }),
  } as GetComponentPropsReturnType;
};
</script>
